import * as Sentry from "@sentry/react";
import { RELOAD_FLAG, isChunkErrorMessage } from "./chunkErrorConstants";

/**
 * Central recovery action for any detected chunk-load failure.
 *
 * If this tab hasn't reloaded yet this session, force a full reload so the
 * browser re-fetches `index.html` with the new asset hashes. If it already
 * has, report to Sentry (the reload didn't fix it — that's the interesting
 * signal worth investigating) and let `<ChunkErrorBoundary>` render its
 * "Update available" fallback.
 *
 * Synchronous: the sessionStorage read+set happens before this function
 * returns. The global `window.error` listener runs before React's commit
 * phase, so by the time `<ChunkErrorBoundary>.componentDidCatch` checks
 * the flag, it will already be set — preventing a double-reload race
 * between this layer and the boundary.
 */
function attemptChunkRecovery(error: unknown): void {
  if (sessionStorage.getItem(RELOAD_FLAG)) {
    Sentry.captureException(error, {
      tags: { chunk_error: "true", reload_already_attempted: "true" },
    });
    return;
  }

  sessionStorage.setItem(RELOAD_FLAG, "true");
  location.reload();
}

/**
 * Installs window-level listeners that detect dynamic-import / chunk-load
 * failures and recover by reloading the page once per tab session.
 *
 * Must be called exactly once, before React mounts (see `main.tsx`). The
 * listeners stay attached for the lifetime of the page — intentional, since
 * the error can fire at any time during navigation.
 *
 * Covers three error surfaces:
 *  - Uncaught synchronous errors (`window.error`) — also bound in capture
 *    phase to catch `<script>` element load failures whose `event.message`
 *    is empty and signal arrives via `event.target`.
 *  - Unhandled promise rejections — most `import()` failures land here.
 *  - bfcache restore (`pageshow.persisted`) — clears the reload flag when
 *    the user navigates back to a working snapshot of the page, so a future
 *    real failure can still recover.
 */
export function initChunkErrorHandler(): void {
  if (typeof window === "undefined") return;

  // Capture-phase listener so we also see resource-load errors on
  // <script>/<link> elements, which don't bubble.
  window.addEventListener(
    "error",
    (event) => {
      // Module-script TypeError path: event.message is populated.
      if (event.message && isChunkErrorMessage(event.message)) {
        attemptChunkRecovery(event.error ?? event.message);
        return;
      }
      // Resource-load path: event.message is empty, signal is the target.
      const target = event.target as HTMLElement | null;
      if (
        target instanceof HTMLScriptElement ||
        target instanceof HTMLLinkElement
      ) {
        attemptChunkRecovery(
          new Error(`Failed to load resource: ${target.outerHTML}`),
        );
      }
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event) => {
    const message =
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason ?? "");
    if (isChunkErrorMessage(message)) {
      attemptChunkRecovery(event.reason);
    }
  });

  // Back/forward cache restore — the page came back from bfcache and is
  // working; reset the flag so a future genuine chunk failure can recover.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      sessionStorage.removeItem(RELOAD_FLAG);
    }
  });
}
